import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarClock, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

// A date + time field with its own calendar dropdown. Replaces the native
// <input type="datetime-local"> on the booking form, which on desktop Safari has
// no calendar popover at all (you type into segments, and the year segment happily
// takes six digits). Value stays the same `YYYY-MM-DDTHH:mm` string the native
// control used, so everything downstream (new Date(value), localDateTime) is untouched.
type Props = {
  value: string; // 'YYYY-MM-DDTHH:mm' or ''
  onChange: (v: string) => void;
  min?: string; // 'YYYY-MM-DD' — earlier days are not offered
  max?: string; // 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm' — later days are not offered
  placeholder?: string;
  defaultHour?: number; // hour used when a day is picked before any time (default 8)
};

const pad = (n: number) => String(n).padStart(2, '0');
const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const SLOTS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 ? 30 : 0;
  const label = `${((h + 11) % 12) + 1}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
  return { value: `${pad(h)}:${pad(m)}`, label };
});

export function DateTimePicker({ value, onChange, min, max, placeholder = 'Pick a date and time', defaultHour = 8 }: Props) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  const datePart = value.slice(0, 10);
  const timePart = value.length >= 16 ? value.slice(11, 16) : '';
  const minKey = min?.slice(0, 10) ?? '';
  const maxKey = max?.slice(0, 10) ?? '';

  // Month being shown: the selected month, else the earliest allowed, else now.
  const [view, setView] = useState<Date>(() => {
    const base = datePart ? new Date(`${datePart}T12:00:00`) : minKey ? new Date(`${minKey}T12:00:00`) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  useEffect(() => {
    if (open && datePart) {
      const d = new Date(`${datePart}T12:00:00`);
      setView(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [open, datePart]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const cells = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const startOffset = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [view]);

  const todayKey = dayKey(new Date());
  const inMonth = (d: Date) => d.getMonth() === view.getMonth();
  const disabled = (d: Date) => {
    const k = dayKey(d);
    return (minKey !== '' && k < minKey) || (maxKey !== '' && k > maxKey);
  };
  const monthLabel = view.toLocaleDateString([], { month: 'long', year: 'numeric' });
  const canPrev = !minKey || dayKey(new Date(view.getFullYear(), view.getMonth(), 0)) >= minKey;
  const canNext = !maxKey || dayKey(new Date(view.getFullYear(), view.getMonth() + 1, 1)) <= maxKey;

  const pickDay = (d: Date) => {
    if (disabled(d)) return;
    onChange(`${dayKey(d)}T${timePart || `${pad(defaultHour)}:00`}`);
  };
  const pickTime = (t: string) => {
    onChange(`${datePart || todayKey}T${t}`);
  };

  const label = value
    ? new Date(value).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`input flex w-full cursor-pointer items-center bg-sky-50/60 pl-10 pr-9 text-left font-medium ${
          value ? 'text-navy-900' : 'text-ink-subtle'
        }`}
      >
        <CalendarClock size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-600" />
        <span className="truncate">{label || placeholder}</span>
        <ChevronDown
          size={16}
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-navy-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute left-0 top-full z-30 mt-2 w-[19.5rem] max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              disabled={!canPrev}
              className="rounded-lg p-1.5 text-navy-700 hover:bg-sky-50 disabled:opacity-30"
              aria-label="Previous month"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="font-display text-sm font-semibold text-navy-900">{monthLabel}</div>
            <button
              type="button"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              disabled={!canNext}
              className="rounded-lg p-1.5 text-navy-700 hover:bg-sky-50 disabled:opacity-30"
              aria-label="Next month"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-2 grid grid-cols-7 text-center text-[11px] font-medium uppercase tracking-wide text-ink-subtle">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-0.5 text-center text-sm">
            {cells.map((d) => {
              const k = dayKey(d);
              const sel = k === datePart;
              const off = disabled(d);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => pickDay(d)}
                  disabled={off}
                  className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                    sel
                      ? 'bg-navy-700 font-semibold text-white'
                      : off
                        ? 'text-slate-300'
                        : inMonth(d)
                          ? 'text-navy-900 hover:bg-sky-50'
                          : 'text-ink-subtle hover:bg-sky-50'
                  } ${k === todayKey && !sel ? 'ring-1 ring-navy-300' : ''}`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-3">
            <label className="text-xs font-medium text-ink-muted">Time</label>
            <select
              className="input h-10 min-h-0 flex-1 py-1"
              value={timePart || `${pad(defaultHour)}:00`}
              onChange={(e) => pickTime(e.target.value)}
            >
              {SLOTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => setOpen(false)} className="btn-primary h-10 min-h-0 px-3 text-sm">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
